import React, { useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Download, Loader2 } from "lucide-react";
import {
  atlasClientName,
  atlasLetterMayStartMatter,
  atlasLetterToFile,
  defaultAtlasSince,
  detectAtlasExtension,
  fetchAtlasLetters,
  localDay,
  markAtlasLettersImported,
  saveLastAtlasRun,
} from "@/lib/atlasLettersExtension";

const OUTCOME_LABEL = {
  read: { text: "Read into matter", tone: "text-emerald-700" },
  "new-matter": { text: "New matter started", tone: "text-emerald-700" },
  "no-matter": { text: "No matter found", tone: "text-amber-700" },
  failed: { text: "Couldn't read", tone: "text-red-700" },
};

const displayDate = (atlasDate) => String(atlasDate || "").split(" ")[0];
const displayDay = (day) => (day ? day.split("-").reverse().join("/") : "no date given");

// Two letters can share a name (same client, type and day); the import matches outcomes
// back to letters by file name, so each one needs its own.
function uniqueNames(letters) {
  const seen = new Map();
  return letters.map((letter) => {
    const count = (seen.get(letter.fileName) || 0) + 1;
    seen.set(letter.fileName, count);
    return count === 1 ? letter : { ...letter, fileName: letter.fileName.replace(/\.pdf$/i, ` (${count}).pdf`) };
  });
}

// "Fetch letters from ATLAS": opens ATLAS in its own window, waits while the person signs in
// (ATLAS asks for a one-time code), then reads the inbox letters into this firm's matters.
// The last run's result is kept by the Aid Planner (result / onResult) so it outlives this panel.
export default function AtlasFetchPanel({ brand = "", busy = false, onImport, onRequisitions = () => [], result = null, onResult = () => {} }) {
  const [extension, setExtension] = useState(undefined); // undefined while checking, null if missing
  const [since, setSince] = useState(() => defaultAtlasSince(brand));
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    detectAtlasExtension().then((found) => { if (live) setExtension(found); });
    return () => { live = false; };
  }, []);

  const start = async () => {
    setRunning(true);
    setError("");
    onResult(null);
    setProgress("Opening ATLAS…");
    try {
      const fetched = await fetchAtlasLetters({ since, onProgress: (p) => setProgress(p.message || "") });
      const letters = uniqueNames(fetched.letters);
      setProgress(letters.length ? `Reading ${letters.length} letter${letters.length === 1 ? "" : "s"} into the Aid Planner…` : "");
      const imported = letters.length
        ? await onImport(letters.map(atlasLetterToFile), {
          newMatterFor: atlasLetterMayStartMatter,
          clientNames: new Map(letters.map((letter) => [letter.fileName, atlasClientName(letter.client)])),
        })
        : { outcomes: [] };
      const byName = new Map((imported?.outcomes || []).map((o) => [o.fileName, o]));
      const rows = letters.map(({ base64, ...letter }) => ({ ...letter, outcome: byName.get(letter.fileName) || { state: "failed" } }));
      // Requisitions aren't marked as read in, so a later fetch picks up their new status.
      markAtlasLettersImported(rows.filter((r) => ["read", "new-matter"].includes(r.outcome.state)).map((r) => r.key));
      const requisitionOutcomes = new Map((onRequisitions(fetched.requisitions) || []).map((o) => [o.key, o]));
      const requisitionRows = fetched.requisitions.map((req) => ({
        ...req,
        documentType: "REQUISITION",
        requisition: { dueOn: req.dueOn, answered: req.answered },
        outcome: requisitionOutcomes.get(req.key) || { state: "failed" },
      }));
      saveLastAtlasRun(brand, localDay());
      onResult({ rows: [...requisitionRows, ...rows], skipped: fetched.skipped, warnings: fetched.warnings });
    } catch (err) {
      setError(err.message || "The ATLAS download stopped.");
    } finally {
      setRunning(false);
      setProgress("");
    }
  };

  const skippedByReason = useMemo(() => {
    const groups = new Map();
    for (const item of result?.skipped || []) groups.set(item.reason, [...(groups.get(item.reason) || []), item]);
    return [...groups.entries()];
  }, [result]);

  const letterRows = result?.rows.filter((r) => !r.requisition) || [];
  const readCount = letterRows.filter((r) => ["read", "new-matter"].includes(r.outcome.state)).length;
  const openRequisitionCount = result?.rows.filter((r) => r.requisition && !r.requisition.answered).length || 0;

  return (
    <section className="rounded-2xl border border-sky-200 bg-white p-5 shadow-sm" aria-label="Fetch letters from ATLAS">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <h3 className="text-lg font-bold text-slate-900">Fetch letters from ATLAS</h3>
          <p className="mt-1 text-sm text-slate-500">
            Opens ATLAS in a new window. Sign in there with your one-time code, and the new letters in the ATLAS
            inbox are downloaded and read into this firm's matters. Approvals, refusals, submitted forms and tax
            invoices are read; client copies and other letters are skipped.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs font-semibold text-slate-600">
            Received since
            <input
              type="date"
              value={since}
              max={localDay()}
              onChange={(event) => setSince(event.target.value)}
              disabled={running}
              className="mt-1 block rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
            />
          </label>
          <button
            type="button"
            onClick={start}
            disabled={!extension || running || busy || !since}
            className="inline-flex items-center gap-2 rounded-lg bg-sky-700 px-4 py-2 text-sm font-bold text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {running ? "Fetching…" : "Fetch ATLAS letters"}
          </button>
        </div>
      </div>

      {extension === null && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-bold">The ATLAS Letters extension isn't in this browser yet.</p>
          <p className="mt-1">
            It needs adding to Chrome once, on each computer that fetches letters. In Chrome open
            {" "}<span className="font-mono">chrome://extensions</span>, turn on Developer mode, choose
            {" "}<span className="font-semibold">Load unpacked</span> and pick the portal's
            {" "}<span className="font-mono">tools/atlas-letters-extension</span> folder, then reload this page.
          </p>
        </div>
      )}

      {running && progress && (
        <div className="mt-4 flex items-center gap-3 rounded-xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-sky-600" />
          <span>{progress}</span>
        </div>
      )}

      {error && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="mt-4 space-y-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            {letterRows.length === 0
              ? "No new letters to read since that date."
              : `${readCount} of ${letterRows.length} letter${letterRows.length === 1 ? "" : "s"} read into the Aid Planner.`}
          </p>
          {openRequisitionCount > 0 && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm font-bold text-red-800">
              {openRequisitionCount} requisition{openRequisitionCount === 1 ? "" : "s"} to answer: see the red banner at the top.
            </p>
          )}
          {result.warnings.map((warning) => (
            <p key={warning} className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">{warning}</p>
          ))}
          {result.rows.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Letter</th>
                    <th className="px-3 py-2">Received</th>
                    <th className="px-3 py-2">ATLAS file</th>
                    <th className="px-3 py-2">Client</th>
                    <th className="px-3 py-2">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {result.rows.map((row) => {
                    const label = OUTCOME_LABEL[row.outcome.state] || OUTCOME_LABEL.failed;
                    const requisitionNote = row.requisition
                      ? row.requisition.answered ? " · already answered in ATLAS" : ` · answer by ${displayDay(row.requisition.dueOn)}`
                      : "";
                    return (
                      <tr key={row.key} className={row.requisition && !row.requisition.answered ? "bg-red-50" : ""}>
                        <td className="px-3 py-2 font-medium text-slate-800">{row.documentType}</td>
                        <td className="px-3 py-2 text-slate-600">{displayDate(row.dateReceived)}</td>
                        <td className="px-3 py-2 text-slate-600">{row.fileExt}</td>
                        <td className="px-3 py-2 text-slate-600">{row.outcome.clientName || row.client}</td>
                        <td className={`px-3 py-2 font-semibold ${label.tone}`}>{label.text}{requisitionNote}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {skippedByReason.length > 0 && (
            <details className="rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-600">
              <summary className="cursor-pointer font-semibold text-slate-700">
                {result.skipped.length} inbox item{result.skipped.length === 1 ? "" : "s"} skipped
              </summary>
              <ul className="mt-2 space-y-2">
                {skippedByReason.map(([reason, items]) => (
                  <li key={reason}>
                    <p className="font-semibold text-slate-700">{reason} ({items.length})</p>
                    <p className="text-xs text-slate-500">
                      {items.slice(0, 12).map((item) => `${item.documentType} · ${item.fileExt} · ${displayDate(item.dateReceived)}`).join("; ")}
                      {items.length > 12 ? "; …" : ""}
                    </p>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
}
